import { Suspense, lazy, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ScrollToTop from "./helpers/scroll-top";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { logoutAndCleanup, refreshUserFromMe } from "./store/slices/auth-slice";
import { store } from "./store/store";
import { injectAuth } from "./helpers/apiFetch";
import { fetchCart } from "./store/slices/cart-api-slice";
import { fetchWishlist } from "./store/slices/wishlist-api-slice";
import SuperAdminRoute from "./helpers/SuperAdminRoute";
import PerfilIncompletoGate from "./helpers/PerfilIncompletoGate";
import appConfig from "./data/config";
import { ToastContainer } from "react-toastify";
import MaintenanceModal from "./components/MaintenanceModal";
import EfemerideModal from "./components/EfemerideModal";
import useTracking from "./tracking/useTracking";
import "react-toastify/dist/ReactToastify.css";

// apiFetch dispatcha el thunk completo en 401 para limpiar todo el estado (auth + cart + cache + cupon),
// no solo el reducer simple de logout.
injectAuth(store.dispatch, logoutAndCleanup);

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost/api/public";
const HOME_COOLDOWN  = 20 * 60 * 1000;
const OTHER_COOLDOWN = 2  * 60 * 60 * 1000;

// Monta el tracking completo (device + sesión + heartbeat + page views) UNA SOLA VEZ
// a nivel app. No se monta en componentes de página individuales (Fase 2 del plan de tracking).
function TrackingMount() {
  const token = useSelector((state) => state.auth?.token ?? null);
  useTracking(token);
  return null;
}

function EfemerideChecker() {
  const location = useLocation();
  const [efemeride, setEfemeride] = useState(null);
  const [visible, setVisible]     = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/efemerides/activa`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data?.id) setEfemeride(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!efemeride) return;
    const isHome  = location.pathname === (process.env.PUBLIC_URL || "") + "/";
    const cooldown = isHome ? HOME_COOLDOWN : OTHER_COOLDOWN;
    const key      = `efemeride_shown_${efemeride.id}`;
    const last     = localStorage.getItem(key);
    const elapsed  = (last && !isNaN(Number(last))) ? Date.now() - Number(last) : Infinity;
    if (elapsed >= cooldown) setVisible(true);
  }, [efemeride, location.pathname]);

  const handleClose = () => {
    if (efemeride) localStorage.setItem(`efemeride_shown_${efemeride.id}`, String(Date.now()));
    setVisible(false);
  };

  if (!visible || !efemeride) return null;
  return <EfemerideModal efemeride={efemeride} onClose={handleClose} />;
}

function StorageSync() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const selectedClientId = useSelector((state) => state.viajante?.selectedClient?.id ?? null);

  // Sincronizar logout entre pestañas — usar el thunk completo para limpiar todo
  // (auth + cart + product cache + cupon). El POST /auth/logout al server fallará
  // porque el token ya fue revocado en la otra pestaña; el try/catch del thunk lo absorbe.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "dp_auth" && e.newValue === null) {
        dispatch(logoutAndCleanup());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [dispatch]);

  // Validar token con el server cuando hay token (boot rehidratado o login nuevo).
  // Si el token está vencido/invalidado → logout completo. Si está OK → cargar carrito.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${appConfig.apiUrl}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        if (cancelled) return;

        if (res.status === 401) {
          dispatch(logoutAndCleanup());
          return;
        }

        if (res.ok) {
          // /auth/me devuelve el user "flat" (sin wrapper) más, cuando el rol es
          // viajante, un campo permisos hermano — actualizamos ambos acá para que
          // el permisos.puede_agregar_sin_stock quede disponible también al
          // rehidratar la sesión al refrescar la página (no solo en login fresco).
          const data = await res.json();
          if (!cancelled) {
            dispatch(refreshUserFromMe({ user: data, permisos: data.permisos }));
          }
          // Perfil incompleto (alta por Google/Facebook sin terminar): el carrito y los
          // favoritos responden 403 hasta completar el registro. PerfilIncompletoGate ya
          // lo está llevando a /completar-registro; acá no se piden.
          if (data.perfil_incompleto) return;
          dispatch(fetchCart());
          dispatch(fetchWishlist());
        }
      } catch {
        // Error de red — no hacemos logout para no romper modo offline. Skip cart load.
      }
    })();

    return () => { cancelled = true; };
  }, [token, selectedClientId, dispatch]);

  return null;
}

// home pages
const HomeFashion = lazy(() => import("./pages/home/HomeFashion"));
const HomeFashionTwo = lazy(() => import("./pages/home/HomeFashionTwo"));
const HomeFashionThree = lazy(() => import("./pages/home/HomeFashionThree"));
const HomeFashionFour = lazy(() => import("./pages/home/HomeFashionFour"));
const HomeFashionFive = lazy(() => import("./pages/home/HomeFashionFive"));
const HomeFashionSix = lazy(() => import("./pages/home/HomeFashionSix"));
const HomeFashionSeven = lazy(() => import("./pages/home/HomeFashionSeven"));
const HomeFashionEight = lazy(() => import("./pages/home/HomeFashionEight"));
const HomeKidsFashion = lazy(() => import("./pages/home/HomeKidsFashion"));
const HomeCosmetics = lazy(() => import("./pages/home/HomeCosmetics"));
const HomeFurniture = lazy(() => import("./pages/home/HomeFurniture"));
const HomeFurnitureTwo = lazy(() => import("./pages/home/HomeFurnitureTwo"));
const HomeFurnitureThree = lazy(() =>
  import("./pages/home/HomeFurnitureThree")
);
const HomeFurnitureFour = lazy(() => import("./pages/home/HomeFurnitureFour"));
const HomeFurnitureFive = lazy(() => import("./pages/home/HomeFurnitureFive"));
const HomeFurnitureSix = lazy(() => import("./pages/home/HomeFurnitureSix"));
const HomeFurnitureSeven = lazy(() =>
  import("./pages/home/HomeFurnitureSeven")
);
const HomeElectronics = lazy(() => import("./pages/home/HomeElectronics"));
const HomeElectronicsTwo = lazy(() =>
  import("./pages/home/HomeElectronicsTwo")
);
const HomeElectronicsThree = lazy(() =>
  import("./pages/home/HomeElectronicsThree")
);
const HomeBookStore = lazy(() => import("./pages/home/HomeBookStore"));
const HomeBookStoreTwo = lazy(() => import("./pages/home/HomeBookStoreTwo"));
const HomePlants = lazy(() => import("./pages/home/HomePlants"));
const HomeFlowerShop = lazy(() => import("./pages/home/HomeFlowerShop"));
const HomeFlowerShopTwo = lazy(() => import("./pages/home/HomeFlowerShopTwo"));
const HomeOrganicFood = lazy(() => import("./pages/home/HomeOrganicFood"));
const HomeOrganicFoodTwo = lazy(() =>
  import("./pages/home/HomeOrganicFoodTwo")
);
const HomeOnepageScroll = lazy(() => import("./pages/home/HomeOnepageScroll"));
const HomeGridBanner = lazy(() => import("./pages/home/HomeGridBanner"));
const HomeAutoParts = lazy(() => import("./pages/home/HomeAutoParts"));
const HomeCakeShop = lazy(() => import("./pages/home/HomeCakeShop"));
const HomeHandmade = lazy(() => import("./pages/home/HomeHandmade"));
const HomePetFood = lazy(() => import("./pages/home/HomePetFood"));
const HomeMedicalEquipment = lazy(() =>
  import("./pages/home/HomeMedicalEquipment")
);
const HomeChristmas = lazy(() => import("./pages/home/HomeChristmas"));
const HomeBlackFriday = lazy(() => import("./pages/home/HomeBlackFriday"));
const HomeBlackFridayTwo = lazy(() =>
  import("./pages/home/HomeBlackFridayTwo")
);
const HomeValentinesDay = lazy(() => import("./pages/home/HomeValentinesDay"));

// shop pages
const ShopGridStandard = lazy(() => import("./pages/shop/ShopGridStandard"));
const ShopGridFilter = lazy(() => import("./pages/shop/ShopGridFilter"));
const ShopGridTwoColumn = lazy(() => import("./pages/shop/ShopGridTwoColumn"));
const ShopGridNoSidebar = lazy(() => import("./pages/shop/ShopGridNoSidebar"));
const ShopGridFullWidth = lazy(() => import("./pages/shop/ShopGridFullWidth"));
const ShopGridRightSidebar = lazy(() =>
  import("./pages/shop/ShopGridRightSidebar")
);
const ShopListStandard = lazy(() => import("./pages/shop/ShopListStandard"));
const ShopListFullWidth = lazy(() => import("./pages/shop/ShopListFullWidth"));
const ShopListTwoColumn = lazy(() => import("./pages/shop/ShopListTwoColumn"));

// product pages
const Product = lazy(() => import("./pages/shop-product/Product"));
const ProductTabLeft = lazy(() =>
  import("./pages/shop-product/ProductTabLeft")
);
const ProductTabRight = lazy(() =>
  import("./pages/shop-product/ProductTabRight")
);
const ProductSticky = lazy(() => import("./pages/shop-product/ProductSticky"));
const ProductSlider = lazy(() => import("./pages/shop-product/ProductSlider"));
const ProductFixedImage = lazy(() =>
  import("./pages/shop-product/ProductFixedImage")
);

// blog pages
const BlogStandard = lazy(() => import("./pages/blog/BlogStandard"));
const BlogNoSidebar = lazy(() => import("./pages/blog/BlogNoSidebar"));
const BlogRightSidebar = lazy(() => import("./pages/blog/BlogRightSidebar"));
const BlogDetailsStandard = lazy(() =>
  import("./pages/blog/BlogDetailsStandard")
);

// other pages
const About = lazy(() => import("./pages/other/About"));
const Returns = lazy(() => import("./pages/other/Returns"));
const FAQs = lazy(() => import("./pages/other/FAQs"));
const PaymentMethods = lazy(() => import("./pages/other/PaymentMethods"));
const DeliveryZones = lazy(() => import("./pages/other/DeliveryZones"));
const Location = lazy(() => import("./pages/other/Location"));
const Contact = lazy(() => import("./pages/other/Contact"));
const MyAccount = lazy(() => import("./pages/other/MyAccount"));
const MyOrders = lazy(() => import("./pages/other/MyOrders"));
const LoginRegister = lazy(() => import("./pages/other/LoginRegister"));
const ForgotPassword = lazy(() => import("./pages/other/ForgotPassword"));
const ResetPassword  = lazy(() => import("./pages/other/ResetPassword"));
const Impersonate    = lazy(() => import("./pages/other/Impersonate"));
const CompletarRegistro = lazy(() => import("./pages/other/CompletarRegistro"));
const RegistrarCliente = lazy(() => import("./pages/other/RegistrarCliente"));

const Cart = lazy(() => import("./pages/other/Cart"));
const Wishlist = lazy(() => import("./pages/other/Wishlist"));
const Compare = lazy(() => import("./pages/other/Compare"));
const Checkout = lazy(() => import("./pages/other/Checkout"));

const NotFound = lazy(() => import("./pages/other/NotFound"));

// Templates demo del theme Flone que NO son parte del flujo activo de Delplastic.
// Se gatean con SuperAdminRoute: cualquier no-super_admin recibe 404. Esto evita
// indexación de Google, scrapers y curiosos en URLs que no deberían estar publicas.
const DEMO_ROUTES = [
  // Home variants
  { path: "/home-fashion-two",        Component: HomeFashionTwo },
  { path: "/home-fashion-three",      Component: HomeFashionThree },
  { path: "/home-fashion-four",       Component: HomeFashionFour },
  { path: "/home-fashion-five",       Component: HomeFashionFive },
  { path: "/home-fashion-six",        Component: HomeFashionSix },
  { path: "/home-fashion-seven",      Component: HomeFashionSeven },
  { path: "/home-fashion-eight",      Component: HomeFashionEight },
  { path: "/home-kids-fashion",       Component: HomeKidsFashion },
  { path: "/home-cosmetics",          Component: HomeCosmetics },
  { path: "/home-furniture",          Component: HomeFurniture },
  { path: "/home-furniture-two",      Component: HomeFurnitureTwo },
  { path: "/home-furniture-three",    Component: HomeFurnitureThree },
  { path: "/home-furniture-four",     Component: HomeFurnitureFour },
  { path: "/home-furniture-five",     Component: HomeFurnitureFive },
  { path: "/home-furniture-six",      Component: HomeFurnitureSix },
  { path: "/home-furniture-seven",    Component: HomeFurnitureSeven },
  { path: "/home-electronics",        Component: HomeElectronics },
  { path: "/home-electronics-two",    Component: HomeElectronicsTwo },
  { path: "/home-electronics-three",  Component: HomeElectronicsThree },
  { path: "/home-book-store",         Component: HomeBookStore },
  { path: "/home-book-store-two",     Component: HomeBookStoreTwo },
  { path: "/home-plants",             Component: HomePlants },
  { path: "/home-flower-shop",        Component: HomeFlowerShop },
  { path: "/home-flower-shop-two",    Component: HomeFlowerShopTwo },
  { path: "/home-organic-food",       Component: HomeOrganicFood },
  { path: "/home-organic-food-two",   Component: HomeOrganicFoodTwo },
  { path: "/home-onepage-scroll",     Component: HomeOnepageScroll },
  { path: "/home-grid-banner",        Component: HomeGridBanner },
  { path: "/home-auto-parts",         Component: HomeAutoParts },
  { path: "/home-cake-shop",          Component: HomeCakeShop },
  { path: "/home-handmade",           Component: HomeHandmade },
  { path: "/home-pet-food",           Component: HomePetFood },
  { path: "/home-medical-equipment",  Component: HomeMedicalEquipment },
  { path: "/home-christmas",          Component: HomeChristmas },
  { path: "/home-black-friday",       Component: HomeBlackFriday },
  { path: "/home-black-friday-two",   Component: HomeBlackFridayTwo },
  { path: "/home-valentines-day",     Component: HomeValentinesDay },
  // Shop variants (active = /shop-grid-standard, NO se gatea)
  { path: "/shop-grid-filter",        Component: ShopGridFilter },
  { path: "/shop-grid-two-column",    Component: ShopGridTwoColumn },
  { path: "/shop-grid-no-sidebar",    Component: ShopGridNoSidebar },
  { path: "/shop-grid-full-width",    Component: ShopGridFullWidth },
  { path: "/shop-grid-right-sidebar", Component: ShopGridRightSidebar },
  { path: "/shop-list-standard",      Component: ShopListStandard },
  { path: "/shop-list-full-width",    Component: ShopListFullWidth },
  { path: "/shop-list-two-column",    Component: ShopListTwoColumn },
  // Product detail variants (active = /product/:id, NO se gatea)
  { path: "/product-tab-left/:id",    Component: ProductTabLeft },
  { path: "/product-tab-right/:id",   Component: ProductTabRight },
  { path: "/product-sticky/:id",      Component: ProductSticky },
  { path: "/product-slider/:id",      Component: ProductSlider },
  { path: "/product-fixed-image/:id", Component: ProductFixedImage },
  // Blog (Delplastic no tiene blog activo)
  { path: "/blog-standard",           Component: BlogStandard },
  { path: "/blog-no-sidebar",         Component: BlogNoSidebar },
  { path: "/blog-right-sidebar",      Component: BlogRightSidebar },
  { path: "/blog-details-standard",   Component: BlogDetailsStandard },
];

const App = () => {
  const [maintenanceActive, setMaintenanceActive] = useState(false);

  // Registrar Service Worker en mismo path que el SW puente del PHP viejo
  // (/js/service-worker.js) para que las PWAs ya instaladas lo detecten como update
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    if (process.env.NODE_ENV !== 'production') return;

    navigator.serviceWorker
      .register('/js/service-worker.js')
      .then((registration) => {
        // Buscar updates cada 60s
        setInterval(() => {
          registration.update().catch(() => {});
        }, 60000);

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'activated') {
              console.log('[SW] Nueva version activada');
            }
          });
        });
      })
      .catch((err) => console.warn('[SW] Registro fallido:', err));
  }, []);

  // PWA analytics — ping una vez por sesión
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    if (sessionStorage.getItem('dp_pwa_pinged')) return;
    sessionStorage.setItem('dp_pwa_pinged', '1');

    (async () => {
      try {
        let deviceId = localStorage.getItem('dp_device_id');
        if (!deviceId) {
          deviceId = crypto.randomUUID
            ? crypto.randomUUID()
            : Math.random().toString(36).slice(2) + Date.now().toString(36);
          localStorage.setItem('dp_device_id', deviceId);
        }

        const ua = navigator.userAgent;
        const platform = /iPad|iPhone|iPod/.test(ua) ? 'iOS'
          : /Android/.test(ua) ? 'Android'
          : /Windows/.test(ua) ? 'Windows'
          : /Mac/.test(ua) ? 'Mac'
          : /Linux/.test(ua) ? 'Linux' : 'Desktop';

        const browser = /Edg\//.test(ua) ? 'Edge'
          : /OPR\/|Opera/.test(ua) ? 'Opera'
          : /Chrome\//.test(ua) ? 'Chrome'
          : /Firefox\//.test(ua) ? 'Firefox'
          : /Safari\//.test(ua) ? 'Safari' : 'unknown';

        const pwaInstalled =
          window.matchMedia('(display-mode: standalone)').matches ||
          navigator.standalone === true;

        // Pedir versión del SW activo
        let version = 'unknown';
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          version = await new Promise(resolve => {
            const ch = new MessageChannel();
            ch.port1.onmessage = e => resolve(e.data?.version ?? 'unknown');
            setTimeout(() => resolve('unknown'), 500);
            navigator.serviceWorker.controller.postMessage({ type: 'GET_VERSION' }, [ch.port2]);
          });
        }

        const apiUrl = process.env.REACT_APP_API_URL ?? 'https://apidp.nonaminda.com/api';
        fetch(`${apiUrl}/pwa/ping`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ device_id: deviceId, version, platform, browser, pwa_installed: pwaInstalled, event_type: 'visit' }),
          keepalive: true,
        }).catch(() => {});
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost/api/public"}/config/mantenimiento`);
        if (response.ok) {
          const data = await response.json();
          setMaintenanceActive(data.mantenimiento_activo === true);
        }
      } catch (error) {
        console.error("Error checking maintenance status:", error);
        // Default: no maintenance mode on fetch error
        setMaintenanceActive(false);
      }
    };

    checkMaintenance();
  }, []);

  return (
      <Router>
        {maintenanceActive && <MaintenanceModal />}
        <TrackingMount />
        <EfemerideChecker />
        <StorageSync />
        <ToastContainer position="bottom-left" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover />
        <ScrollToTop>
          <Suspense
            fallback={
              <div className="flone-preloader-wrapper">
                <div className="flone-preloader">
                  <span></span>
                  <span></span>
                </div>
              </div>
            }
          >
            <PerfilIncompletoGate>
            <Routes>
              <Route
                path={process.env.PUBLIC_URL + "/"}
                element={<HomeFashion/>}
              />

              {/* Homepage activa (alias /home-fashion mantiene retrocompatibilidad) */}
              <Route
                path={process.env.PUBLIC_URL + "/home-fashion"}
                element={<HomeFashion/>}
              />

              {/* Shop activo */}
              <Route
                path={process.env.PUBLIC_URL + "/products"}
                element={<ShopGridStandard/>}
              />

              {/* Producto detalle activo */}
              <Route
                path={process.env.PUBLIC_URL + "/product/:id"}
                element={<Product />}
              />

              {/* Rutas demo del theme Flone — solo super_admin (404 para el resto) */}
              {DEMO_ROUTES.map(({ path, Component }) => (
                <Route
                  key={path}
                  path={process.env.PUBLIC_URL + path}
                  element={
                    <SuperAdminRoute>
                      <Component/>
                    </SuperAdminRoute>
                  }
                />
              ))}

              {/* Other pages */}
              <Route
                path={process.env.PUBLIC_URL + "/about"}
                element={<About/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/Location"}
                element={<Location />}
              /> 
              <Route
                path={process.env.PUBLIC_URL + "/Returns"}
                element={<Returns/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/PaymentMethods"}
                element={<PaymentMethods/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/FAQs"}
                element={<FAQs/>}
              />
               <Route
                path={process.env.PUBLIC_URL + "/DeliveryZones"}
                element={<DeliveryZones/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/contact"}
                element={<Contact/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/my-account"}
                element={<MyAccount/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/my-orders"}
                element={<MyOrders/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/login-register"}
                element={<LoginRegister/>}
              />
              <Route path={process.env.PUBLIC_URL + "/forgot-password"} element={<ForgotPassword />} />
              <Route path={process.env.PUBLIC_URL + "/reset-password"} element={<ResetPassword />} />
              <Route path={process.env.PUBLIC_URL + "/impersonate"} element={<Impersonate />} />
              <Route path={process.env.PUBLIC_URL + "/completar-registro"} element={<CompletarRegistro />} />
              <Route path={process.env.PUBLIC_URL + "/registrar-cliente"} element={<RegistrarCliente />} />

              <Route
                path={process.env.PUBLIC_URL + "/cart"}
                element={<Cart/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/wishlist"}
                element={<Wishlist/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/compare"}
                element={<Compare/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/checkout"}
                element={<Checkout/>}
              /> 

              <Route path="*" element={<NotFound/>} />
            </Routes>
            </PerfilIncompletoGate>
          </Suspense>
        </ScrollToTop>
      </Router>
  );
};

export default App;